import { useMutation } from '@apollo/client';
import type { PreferencesSet } from '@risksmart-app/shared/knock/schemas';
import type { UpdateNotificationPreferencesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetNotificationPreferencesDocument,
  UpdateNotificationPreferencesDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useUpdateNotificationPreferencesTRPC } from './useUpdateNotificationPreferencesTRPC';

type UpdateNotificationPreferencesInput = { preferenceSet: PreferencesSet };

/**
 * Flag-gated wrapper for updating the current user's Knock notification
 * preferences. Consumers import THIS, never the `*TRPC` twin (see
 * packages/web/CLAUDE.md — Mutation Hook Wrapper Pattern).
 */
export const useUpdateNotificationPreferences = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateNotificationPreferencesDocument,
    {
      refetchQueries: [GetNotificationPreferencesDocument],
    }
  );

  const trpcMutation = useUpdateNotificationPreferencesTRPC();

  const updateNotificationPreferences = async (
    variables: UpdateNotificationPreferencesInput
  ): Promise<UpdateNotificationPreferencesMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateNotificationPreferences(variables);
    }

    const result = await updateGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to update notification preferences');
    }

    return result.data;
  };

  return {
    updateNotificationPreferences,
    loading: trpcEnabled ? trpcMutation.loading : graphqlState.loading,
    error: trpcEnabled ? trpcMutation.error : graphqlState.error,
  };
};
